import { useEffect, useState } from 'react'
import { RedeemInvitation } from '../services/peerToPeer/InvitationService'
import { type InvitationRedemptionResult } from '../models/peerToPeer/Invitations'
import { clearInviteToken, getInviteToken } from '../pages/pageComponents/peerToPeer/auth/inviteToken'
import { useAuth } from './use-auth'

export type InviteRedemptionState =
  | { status: 'waiting' }
  | { status: 'none' }
  | { status: 'accepted' }
  | { status: 'failed', result: InvitationRedemptionResult }

const KEEP_TOKEN_OUTCOMES = new Set(['EmailMismatch', 'NotOpen'])

/**
 * Once someone is signed in, accepts the invitation whose link they arrived through. The token
 * survives a mismatch so the invitee can sign out and back in with the invited address.
 */
export const useP2PInviteRedemption = (): InviteRedemptionState => {
  const auth = useAuth()
  const [state, setState] = useState<InviteRedemptionState>({ status: 'waiting' })
  const signedIn = !auth.loading && auth.user != null

  useEffect(() => {
    if (auth.loading) return
    if (!signedIn) {
      setState({ status: 'none' })
      return
    }

    const token = getInviteToken()
    if (token == null) {
      setState({ status: 'none' })
      return
    }

    let isCurrent = true
    const redeem = async (): Promise<void> => {
      setState({ status: 'waiting' })
      const { success, response } = await RedeemInvitation(token)
      if (!isCurrent) return

      if (!success || response == null) {
        setState({ status: 'failed', result: { outcome: 'NotFound', invitedEmail: null } })
        return
      }

      if (!KEEP_TOKEN_OUTCOMES.has(response.outcome)) clearInviteToken()

      if (response.outcome === 'Accepted' || response.outcome === 'AlreadyAccepted') {
        await auth.setCurrentUser()
        if (isCurrent) setState({ status: 'accepted' })
        return
      }
      setState({ status: 'failed', result: response })
    }

    void redeem()
    return () => { isCurrent = false }
  }, [auth.loading, signedIn])

  return state
}
